import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Squad } from '../../../types';
import { db } from '../../../shared/storage/firebase';
import { useAuth } from '../../auth/contexts/AuthContext';
import { hasAdminPermission } from '../../auth/adminPermissions';
import SquadCalendar from '../../squads/components/SquadCalendar';
import AdminCurriculumTab from './AdminCurriculumTab';
import AdminLearningCenterTab from './AdminLearningCenterTab';
import '../styles/AdminPanel.css';

type DelegatedTab = 'calendar' | 'learning' | 'curriculum';

const DelegatedAdminPanel: React.FC = () => {
  const { userData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [squads, setSquads] = useState<Squad[]>([]);
  const tabs = useMemo(() => [
    hasAdminPermission(userData, 'curriculum.manage') ? { id: 'calendar' as const, label: 'Calendar' } : null,
    hasAdminPermission(userData, 'certifications.manage') ? { id: 'learning' as const, label: 'Certifications' } : null,
    hasAdminPermission(userData, 'curriculum.manage') ? { id: 'curriculum' as const, label: 'Curriculum' } : null,
  ].filter((tab): tab is { id: DelegatedTab; label: string } => Boolean(tab)), [userData]);
  const requested = String(new URLSearchParams(location.search).get('adminTab') || (location.state as any)?.adminTab || '');
  const initial = tabs.find(tab => tab.id === requested)?.id || tabs[0]?.id || 'curriculum';
  const [activeTab, setActiveTab] = useState<DelegatedTab>(initial);

  useEffect(() => {
    if (!tabs.some(tab => tab.id === activeTab) && tabs[0]) setActiveTab(tabs[0].id);
  }, [activeTab, tabs]);

  useEffect(() => {
    if (activeTab !== 'calendar' || !hasAdminPermission(userData, 'curriculum.manage')) return;
    void getDocs(collection(db, 'squads')).then(snapshot => {
      setSquads(snapshot.docs.map(item => ({ id: item.id, ...item.data() } as Squad)));
    }).catch(error => console.error('Could not load squads for the curriculum calendar.', error));
  }, [activeTab, userData]);

  return (
    <div className="admin-panel">
      <div className="admin-ribbon">
        <button type="button" className="admin-back-btn" onClick={() => navigate('/app/squads')}>← Back</button>
        <div className="admin-tabs-ribbon">
          {tabs.map(tab => <button key={tab.id} type="button" className={`tab-button ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}><span className="tab-label">{tab.label}</span></button>)}
        </div>
        <h1 className="admin-ribbon-title">Admin Tools</h1>
      </div>
      <div className="admin-content">
        {activeTab === 'calendar' && hasAdminPermission(userData, 'curriculum.manage') && (
          <div className="calendar-admin-tab">
            <div className="admin-content-card">
              <SquadCalendar
                squads={squads}
                onSquadOpen={(squadId) => navigate(`/app/squads/${squadId}?tab=portfolios`)}
              />
            </div>
          </div>
        )}
        {activeTab === 'learning' && hasAdminPermission(userData, 'certifications.manage') && <AdminLearningCenterTab />}
        {activeTab === 'curriculum' && hasAdminPermission(userData, 'curriculum.manage') && <AdminCurriculumTab />}
      </div>
    </div>
  );
};

export default DelegatedAdminPanel;
